import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import { APP_SHELL_SEARCH_SX } from '../theme/appShell';

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const ROUTE_MAP = {
  customer: (uuid) => `/customer/${uuid}/view`,
  inventory: (uuid) => `/inventory/${uuid}/edit`,
  order: (uuid) => `/orders/${uuid}`,
  enquiry: (uuid) => `/enquiries/${uuid}`,
  invoice: (uuid) => `/invoice/${uuid}`,
};

const TYPE_META = {
  customer: { label: 'Customer', color: '#1976d2' },
  inventory: { label: 'Inventory', color: '#2e7d32' },
  order: { label: 'Order', color: '#e65100' },
  enquiry: { label: 'Enquiry', color: '#7b1fa2' },
  invoice: { label: 'Invoice', color: '#6d4c41' },
};

const GlobalSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const debouncedQuery = useDebounce(query, 500);

  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }

    const fetchResults = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await axiosInstance.get('/search', { params: { q: debouncedQuery.trim() } });
        if (res.data?.success) {
          setResults(res.data.data.results || []);
          setOpen(true);
        }
      } catch (err) {
        console.error('Global search failed:', err);
        setError('Search failed. Please try again.');
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [debouncedQuery]);

  const handleSelect = (result) => {
    const routeFn = ROUTE_MAP[result.type];
    if (routeFn) navigate(routeFn(result.uuid));
    setOpen(false);
    setQuery('');
    setResults([]);
  };

  const handleBlur = () => {
    setTimeout(() => setOpen(false), 150);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '10px 12px 12px',
        ...APP_SHELL_SEARCH_SX,
      }}
    >
      <TextField
        variant="outlined"
        placeholder="Search customers, orders, inventory..."
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!e.target.value) setOpen(false);
        }}
        onFocus={() => {
          if (results.length > 0) setOpen(true);
        }}
        onBlur={handleBlur}
        inputProps={{ 'aria-label': 'global search' }}
        InputProps={{
          endAdornment: loading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null,
        }}
        sx={{
          width: '400px',
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'white',
            borderRadius: '20px',
            '& fieldset': { borderColor: 'transparent' },
            '&:hover fieldset': { borderColor: 'transparent' },
            '&.Mui-focused fieldset': { borderColor: 'transparent' },
          },
          '& .MuiInputBase-input': { padding: '10px 15px' },
        }}
      />

      {open && (
        <Paper
          onMouseDown={(e) => e.preventDefault()}
          sx={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '400px',
            zIndex: 1300,
            maxHeight: '400px',
            overflowY: 'auto',
            mt: '4px',
            boxShadow: 4,
          }}
        >
          {error && (
            <Typography sx={{ p: 2, color: 'error.main', fontSize: '0.875rem' }}>
              {error}
            </Typography>
          )}

          {!error && results.length === 0 && !loading && (
            <Typography sx={{ p: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
              No results found for "{debouncedQuery}"
            </Typography>
          )}

          {results.length > 0 && (
            <List dense disablePadding>
              {results.map((result) => {
                const meta = TYPE_META[result.type] || {};
                return (
                  <ListItemButton
                    key={`${result.type}-${result.uuid}`}
                    onClick={() => handleSelect(result)}
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      alignItems: 'flex-start',
                      py: 1,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        minWidth: '72px',
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        color: 'white',
                        backgroundColor: meta.color,
                        borderRadius: '4px',
                        px: '6px',
                        py: '2px',
                        mr: 1.5,
                        mt: '3px',
                        textAlign: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {meta.label}
                    </Box>
                    <ListItemText
                      primary={result.label}
                      secondary={result.sublabel}
                      primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
                      secondaryTypographyProps={{ fontSize: '0.75rem' }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default GlobalSearch;
